/*
  Attaquer fit finder.

  The recommendation is a deterministic lookup against the product's own
  size_guide metaobject. There is no network call and no model in this path, so
  an answer is instant, reproducible, and cannot name a size that does not exist.

  The rules below mirror fit-finder/engine.py exactly. test-vectors.json is
  generated from the Python engine and replayed against this file by
  fit-finder/test_parity.mjs, so the two cannot drift silently.
*/
(function () {
  "use strict";

  var SIZE_ORDER = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "4XL", "5XL"];
  var ALIASES = {
    XSMALL: "XS", "X-SMALL": "XS", "EXTRA SMALL": "XS",
    SMALL: "S", SML: "S",
    MEDIUM: "M", MED: "M",
    LARGE: "L", LRG: "L", LGE: "L",
    XLARGE: "XL", "X-LARGE": "XL", "EXTRA LARGE": "XL", XLG: "XL",
    XXLARGE: "XXL", "XX-LARGE": "XXL", "2XL": "XXL", "2X": "XXL",
    "3XL": "XXXL", "3X": "XXXL",
    XXSMALL: "XXS", "XX-SMALL": "XXS", "2XS": "XXS", "3XS": "XXXS"
  };

  // Fitted from 17,759 orders and 612 customer size transitions.
  // See fit-finder/FINDINGS.md section E.
  var CALIBRATION = {
    "mens-jersey": 0.08,
    "mens-outerwear": 0.08,
    "womens-jersey": 0.08,
    "womens-bibs": 0.03,
    "mens-bibs": 0.03,
    "gloves": 0.03,
    "warmers": 0.03,
    _default: 0.0
  };
  var INTENT_BIAS = { race: -0.15, performance: 0.0, relaxed: 0.2 };

  var MEASURE_KEYS = { chest: "chest", waist: "waist", hip: "hip", hips: "hip", weight: "weight", knuckles: "knuckles", "hand length": "hand_length" };
  var MEASURE_LABEL = { chest: "chest", waist: "waist", hip: "hip", weight: "weight", knuckles: "knuckle circumference", hand_length: "hand length" };

  var CM_PER_IN = 2.54;
  var KG_PER_LB = 0.45359237;

  /* Countries that read height in feet and weight in pounds. Everywhere else
     leads with cm and kg. */
  var IMPERIAL_REGIONS = { US: 1, LR: 1, MM: 1 };

  /* Liquid knows the market the page was rendered for, but product pages are
     cached and served across markets, so that answer can belong to someone
     else. The visitor's own locale is the more reliable signal, so it wins
     when it names a region; the rendered market is the fallback. */
  function defaultUnitFor(rendered) {
    var region = "";
    try {
      var lang = (navigator.languages && navigator.languages[0]) || navigator.language || "";
      if (window.Intl && Intl.Locale) {
        region = (new Intl.Locale(lang).region || "").toUpperCase();
      }
      if (!region) {
        var m = /[-_]([A-Za-z]{2})(?:[-_]|$)/.exec(lang);
        region = m ? m[1].toUpperCase() : "";
      }
    } catch (e) { region = ""; }
    if (region) return IMPERIAL_REGIONS[region] ? "imperial" : "metric";
    return rendered === "imperial" ? "imperial" : "metric";
  }

  function normaliseSize(label) {
    if (label === null || label === undefined) return null;
    var s = String(label).trim().toUpperCase().replace(/[–—]/g, "-").replace(/\s+/g, " ");
    if (SIZE_ORDER.indexOf(s) !== -1) return s;
    var compact = s.replace(/[\s-]/g, "");
    if (SIZE_ORDER.indexOf(compact) !== -1) return compact;
    if (ALIASES[s]) return ALIASES[s];
    if (ALIASES[compact]) return ALIASES[compact];
    return null;
  }

  function sizeRank(label) {
    var tok = normaliseSize(label);
    var i = SIZE_ORDER.indexOf(tok);
    return i === -1 ? null : i;
  }

  function parseCell(cell) {
    if (cell === null || cell === undefined) return null;
    if (typeof cell === "object") {
      var metric = parseCell(cell.cm);
      if (!metric) return null;
      metric.imperial = parseCell(cell["in"]);
      return metric;
    }
    var text = String(cell).trim().replace(/[–—]/g, "-");
    if (!text || text === "-") return null;

    var unit = null;
    var um = text.toLowerCase().match(/\b(cm|kg|lb|in)\b/);
    if (um) unit = um[1];
    var stripped = text.replace(/\b(cm|kg|lb|in|CM|KG|LB|IN)\b/g, "").trim();

    var num = function (t) { var v = parseFloat(t); return isNaN(v) ? null : v; };

    if (stripped.charAt(0) === "<") { var a = num(stripped.slice(1)); return a === null ? null : { min: null, max: a, unit: unit }; }
    if (stripped.charAt(0) === ">") { var b = num(stripped.slice(1)); return b === null ? null : { min: b, max: null, unit: unit }; }
    if (stripped.slice(-1) === "+") { var c = num(stripped.slice(0, -1)); return c === null ? null : { min: c, max: null, unit: unit }; }

    var parts = stripped.split("-").map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length === 2) {
      var lo = num(parts[0]), hi = num(parts[1]);
      if (lo !== null && hi !== null) return { min: lo, max: hi, unit: unit };
    }
    if (parts.length === 1) {
      var v = num(parts[0]);
      if (v !== null) return { min: v, max: v, unit: unit };
    }
    return null;
  }

  /* Turn the metaobject's {units, columns, rows} into {measure: {size: band}}.

     weightFallback carries the mens-bibs chart for mens-jersey and warmers,
     which publish no weight column of their own but share an identical chest
     and waist grade with it. */
  function buildChart(handle, measurements, weightFallback) {
    var columns = (measurements && measurements.columns) || [];
    var rows = (measurements && measurements.rows) || [];
    var measures = {};
    var sizes = [];

    rows.forEach(function (row) {
      sizes.push(normaliseSize(row[0]) || String(row[0]).trim());
    });

    columns.forEach(function (col, idx) {
      if (idx === 0) return;
      var key = MEASURE_KEYS[String(col).trim().toLowerCase()];
      if (!key) return;
      rows.forEach(function (row) {
        if (idx >= row.length) return;
        var size = normaliseSize(row[0]) || String(row[0]).trim();
        var band = parseCell(row[idx]);
        if (band) {
          measures[key] = measures[key] || {};
          measures[key][size] = band;
        }
      });
    });

    var borrowed = false;
    if (!measures.weight && weightFallback) {
      var fb = buildChart(handle, weightFallback, null);
      if (fb.measures.weight) {
        measures.weight = fb.measures.weight;
        borrowed = true;
      }
    }

    return { handle: handle, sizes: sizes, measures: measures, weightBorrowed: borrowed };
  }

  function ordered(bands) {
    return Object.keys(bands)
      .map(function (s) { return [s, bands[s]]; })
      .sort(function (a, b) {
        var ra = sizeRank(a[0]); var rb = sizeRank(b[0]);
        return (ra === null ? 99 : ra) - (rb === null ? 99 : rb);
      });
  }

  /* Overlapping bands resolve to the larger size. Rounding gaps resolve up. */
  function matchMeasure(bands, value) {
    var list = ordered(bands);
    if (!list.length) return { size: null, pos: null, status: "none" };

    var hits = [];
    list.forEach(function (entry) {
      var band = entry[1];
      var lo = band.min === null ? -Infinity : band.min;
      var hi = band.max === null ? Infinity : band.max;
      if (value >= lo && value <= hi) {
        var span = (hi > lo && hi !== Infinity && lo !== -Infinity) ? hi - lo : 0;
        hits.push({ size: entry[0], pos: span ? (value - lo) / span : 0.5 });
      }
    });
    if (hits.length) {
      var last = hits[hits.length - 1];
      return { size: last.size, pos: last.pos, status: "in" };
    }

    var firstLo = list[0][1].min;
    var lastHi = list[list.length - 1][1].max;
    if (firstLo !== null && value < firstLo) return { size: list[0][0], pos: 0, status: "below" };
    if (lastHi !== null && value > lastHi) return { size: list[list.length - 1][0], pos: 1, status: "above" };

    for (var i = 0; i < list.length - 1; i++) {
      var b1 = list[i][1], b2 = list[i + 1][1];
      if (b1.max !== null && b2.min !== null && b1.max < value && value < b2.min) {
        return { size: list[i + 1][0], pos: 0, status: "gap" };
      }
    }
    return { size: null, pos: null, status: "none" };
  }

  function fitToAvailable(size, available) {
    var canon = {};
    available.forEach(function (label) {
      var tok = normaliseSize(label);
      if (tok && !canon[tok]) canon[tok] = label;
    });
    var keys = Object.keys(canon);
    if (!keys.length) return { size: null, note: "Product sizes could not be matched to the chart." };
    if (canon[size]) return { size: canon[size], note: null };

    var target = sizeRank(size);
    var ranked = keys.sort(function (a, b) { return sizeRank(a) - sizeRank(b); });
    var bigger = ranked.filter(function (t) { return sizeRank(t) > target; });
    var smaller = ranked.filter(function (t) { return sizeRank(t) < target; });
    if (bigger.length) {
      return { size: canon[bigger[0]], note: size + " is not stocked for this product, so we have shown the next size up." };
    }
    return { size: canon[smaller[smaller.length - 1]], note: size + " is not stocked for this product, so we have shown the largest available." };
  }

  function recommend(chart, measurements, intent, available, derived, unit) {
    derived = derived || {};
    unit = unit || "metric";
    var bias = (INTENT_BIAS[intent] || 0) + (CALIBRATION[chart.handle] !== undefined ? CALIBRATION[chart.handle] : CALIBRATION._default);
    var notes = [];
    var per = {};

    Object.keys(measurements || {}).forEach(function (measure) {
      var value = measurements[measure];
      if (value === null || value === undefined || isNaN(value)) return;
      var bands = chart.measures[measure];
      if (!bands) return;
      var m = matchMeasure(bands, value);
      if (!m.size) return;
      per[measure] = { size: m.size, pos: m.pos, status: m.status, value: value };
      if (m.status === "above") notes.push("Your " + MEASURE_LABEL[measure] + " is above the largest size we publish.");
      if (m.status === "below") notes.push("Your " + MEASURE_LABEL[measure] + " is below the smallest size we publish.");
    });

    var keys = Object.keys(per);
    if (!keys.length) {
      return { size: null, confidence: "none", reason: "We need at least one measurement to match against this chart.", notes: notes, per: per };
    }

    /* Ties break toward whichever sits higher in its band. */
    keys.sort(function (a, b) {
      var d = sizeRank(per[a].size) - sizeRank(per[b].size);
      return d !== 0 ? d : (per[a].pos || 0) - (per[b].pos || 0);
    });

    /* The published weight band over-sizes every model on the roster, so weight
       decides only when nothing else was given. */
    var body = keys.filter(function (k) { return k !== "weight"; });
    var pool = body.length ? body : keys;
    var driver = pool[pool.length - 1];
    var driving = per[driver];
    var base = driving.size;

    var bumped = false;
    if ((driving.status === "in" || driving.status === "gap") && driving.pos !== null && driving.pos >= 1 - bias) {
      var list = ordered(chart.measures[driver]).map(function (e) { return e[0]; });
      var idx = list.indexOf(base);
      if (idx > -1 && idx + 1 < list.length) { base = list[idx + 1]; bumped = true; }
    }

    if (body.length && per.weight && per.weight.size !== base) {
      notes.push("Your weight of " + sayWeight(per.weight.value, unit) + " alone would say " +
        per.weight.size + ", but a garment fits to your body, so we have gone with " + base + ".");
    }
    if (body.length > 1) {
      var smallest = body[0];
      if (sizeRank(base) - sizeRank(per[smallest].size) >= 2) {
        notes.push("Your " + MEASURE_LABEL[smallest] + " sits in " + per[smallest].size +
          ", so " + base + " will be roomy through the " + MEASURE_LABEL[smallest] +
          ". We size on the " + MEASURE_LABEL[driver] + " because that is what has to fit.");
      }
    }

    var seen = {};
    keys.forEach(function (k) { seen[per[k].size] = true; });
    var ranks = Object.keys(seen).map(sizeRank).filter(function (r) { return r !== null; });
    var spread = ranks.length ? Math.max.apply(null, ranks) - Math.min.apply(null, ranks) : 0;

    var confidence;
    if (keys.length >= 2 && spread === 0) confidence = "high";
    else if (keys.length >= 2 && spread === 1) confidence = "medium";
    else if (keys.length === 1 && driver !== "weight") confidence = "medium";
    else confidence = "low";
    keys.forEach(function (k) { if (per[k].status === "above" || per[k].status === "below") confidence = "low"; });
    /* A brand-inferred measurement is that brand's assumption, never a fact. */
    if (confidence === "high" && keys.some(function (k) { return derived[k]; })) confidence = "medium";

    var final = base;
    if (available && available.length) {
      var fitted = fitToAvailable(base, available);
      final = fitted.size;
      if (fitted.note) notes.push(fitted.note);
    }

    return {
      size: final,
      recommended: base,
      confidence: confidence,
      bumped: bumped,
      driver: driver,
      reason: explain(per, driving, base, bumped, intent, derived, pool, unit),
      notes: notes,
      per: per
    };
  }

  function explain(per, driving, size, bumped, intent, derived, keys, unit) {
    derived = derived || {};
    unit = unit || "metric";
    var shown = keys || Object.keys(per);
    var own = [], inferred = [];
    Object.keys(per).forEach(function (m) {
      if (shown.indexOf(m) === -1) return;
      var said = sayValue(m, per[m].value, unit);
      if (derived[m]) {
        inferred.push("a " + said + " " + MEASURE_LABEL[m] + " sits in " + per[m].size);
      } else {
        own.push(MEASURE_LABEL[m] + " of " + said + " sits in " + per[m].size);
      }
    });

    var parts = [];
    if (own.length) parts.push("Your " + own.join(", ") + ".");
    if (inferred.length) {
      var joined = inferred.join(", and ");
      parts.push(joined.charAt(0).toUpperCase() + joined.slice(1) + ".");
    }
    var lead = parts.join(" ");
    if (bumped) {
      return lead + " You are near the top of " + driving.size + ", and Attaquer's kit is cut close to the body, so we would put you in " + size + ".";
    }
    if (intent === "race") return lead + " You asked for a race fit, so " + size + " is the closest cut.";
    return lead + " That puts you in " + size + ".";
  }

  function round1(n) { return Math.round(n * 10) / 10; }

  /* Say the number back in the units the shopper typed it in. Reading "your
     chest of 97cm" after entering inches reads like we misheard them. */
  function sayLength(cm, unit) {
    return unit === "imperial" ? fmtLength(cm, unit) : round1(cm) + "cm";
  }

  function sayWeight(kg, unit) {
    return unit === "imperial" ? Math.round(kg / KG_PER_LB) + "lb" : round1(kg) + "kg";
  }

  function sayValue(measure, value, unit) {
    return measure === "weight" ? sayWeight(value, unit) : sayLength(value, unit);
  }

  function sayRange(loCm, hiCm, unit) {
    if (unit !== "imperial") return Math.round(loCm) + " to " + Math.round(hiCm) + "cm";
    return Math.round(loCm / CM_PER_IN) + " to " + fmtLength(hiCm, unit);
  }

  /* Loose on purpose: model_size is hand-typed and the store holds a dozen
     shapes of it. Audited by fit-finder/audit_model_notes.py. */
  var MODEL_SIZE_RE = /(?:wears?|wearing)\s+(?:an?\s+)?(?:size\s+)?([A-Za-z0-9]{1,4}(?:\s*[\/-]\s*[A-Za-z0-9]{1,4})?)|is\s+an?\s+size\s+([A-Za-z0-9]{1,4})/i;

  function modelSentenceSize(sentence) {
    var m = MODEL_SIZE_RE.exec(sentence);
    if (!m) return null;
    var raw = m[1] || m[2] || "";
    var parts = raw.split(/[\/-]/).map(normaliseSize).filter(Boolean);
    return parts.length ? parts : null;
  }

  function modelNoteFor(note, size) {
    if (!note || !size) return null;
    /* Banded sizes read as their parts: "S/M" against a note that says S. */
    var want = String(size).split(/[\/\-\s]/).map(normaliseSize).filter(Boolean);
    if (!want.length) return null;

    var text = String(note).replace(/\s+/g, " ").replace(/\.(?=[A-Z])/g, ". ").trim();
    var segments = text.split(/\.\s+|\s+and\s+(?=[A-Z][a-z]+\s+(?:is|wears)\b)/);

    var kept = [], seen = {};
    segments.forEach(function (seg) {
      seg = seg.trim().replace(/[.\s]+$/, "");
      if (!seg || seen[seg]) return;
      var sizes = modelSentenceSize(seg);
      if (!sizes || !sizes.some(function (s) { return want.indexOf(s) !== -1; })) return;
      seen[seg] = true;
      kept.push(seg);
    });
    return kept.length ? kept.join(". ") + "." : null;
  }

  /* ---------------------------------------------------- shoe charts ------ */

  /* Socks and shoe covers publish a size against one or more shoe-size systems
     (US men's and women's for socks; AU men's, AU women's and EU for shoe
     covers). Read the column headings to find which systems exist, then match
     the shopper's number to a band. */
  function shoeSystems(measurements) {
    var columns = (measurements && measurements.columns) || [];
    var rows = (measurements && measurements.rows) || [];
    var systems = [];
    columns.forEach(function (col, idx) {
      if (idx === 0) return;
      var bands = {}, any = false;
      rows.forEach(function (row) {
        var band = parseCell(row[idx]);
        if (band) { bands[String(row[0]).trim()] = band; any = true; }
      });
      if (any) systems.push({ label: String(col).trim(), bands: bands });
    });
    return systems;
  }

  function shoeRange(system) {
    var lo = Infinity, hi = -Infinity;
    Object.keys(system.bands).forEach(function (k) {
      var b = system.bands[k];
      if (b.min !== null) { lo = Math.min(lo, b.min); hi = Math.max(hi, b.min); }
      if (b.max !== null) { lo = Math.min(lo, b.max); hi = Math.max(hi, b.max); }
    });
    if (!isFinite(lo)) return { min: 3, max: 15 };
    return { min: Math.floor(lo), max: Math.ceil(hi) + 2 };
  }

  /* ------------------------------------------------- derived charts ------ */

  /* Base layers publish no measurements. Their chart says "base layer sizing is
     relative to your regular jersey size" and then tabulates it:

        Base Layer Size | Men's Jersey Size | Women's Jersey Size
        XS-XXS          | XXS to XS         | XXS to S
        S-M             | S to M            | M to XL
        L-XL            | L to XXL          | -

     So we size the rider against the jersey chart, then read that table
     backwards to land on a base layer size. Nothing is invented; the mapping is
     Attaquer's own. */
  function baseLayerMap(measurements, group) {
    var rows = (measurements && measurements.rows) || [];
    var col = group === "women" ? 2 : 1;
    var map = {};
    rows.forEach(function (row) {
      var label = String(row[0] || "").trim();
      var span = String(row[col] || "").trim();
      if (!label || !span || span === "-" || span === "\u2014") return;
      var ends = span.split(/\s+to\s+/i).map(function (x) { return normaliseSize(x); });
      var lo = sizeRank(ends[0]);
      var hi = sizeRank(ends[ends.length - 1]);
      if (lo === null || hi === null) return;
      for (var r = Math.min(lo, hi); r <= Math.max(lo, hi); r++) {
        map[SIZE_ORDER[r]] = label;
      }
    });
    return map;
  }


  /* ---------------------------------------------------------------- UI ---- */

  /* Which of the chart's own measurements to ask for, in the order that matters
     for that garment. Bibs are sized on waist first, gloves on knuckles, tops on
     chest. Anything the chart does not publish is dropped, so we never ask a
     question the chart cannot answer. */
  var OPTIONAL_MEASURES = {
    "mens-bibs": ["waist", "hip"],
    "womens-bibs": ["waist", "hip"],
    "mens-jersey": ["chest", "waist"],
    "womens-jersey": ["chest", "waist"],
    "mens-outerwear": ["chest", "waist"],
    "womens-outerwear": ["chest", "waist"],
    "warmers": ["chest", "waist"],
    "gloves": ["knuckles", "hand_length"]
  };

  /* The men's bib chart grades the hip at waist plus 14cm at every size, so for
     any man at or under that it cannot change the answer, and it can only ever
     push up. Offered from the result instead, where the riders it does move
     (over +16) can still reach it. */
  var DEFERRED_MEASURES = {
    "mens-bibs": ["hip"]
  };

  var MEASURE_STEP = {
    chest: { title: "Do you know your chest measurement?",
             sub: "Optional. Measure around the fullest part, under the arms.",
             metric: { min: 70, max: 130, majorEvery: 10 }, imperial: { min: 28, max: 52, majorEvery: 4 },
             start: 99 },
    waist: { title: "And your waist?", titleFirst: "What is your waist measurement?",
             sub: "Optional. Measure around the narrowest part.",
             metric: { min: 55, max: 125, majorEvery: 10 }, imperial: { min: 22, max: 49, majorEvery: 4 },
             start: 84 },
    hip:   { title: "And your hips?", titleFirst: "What is your hip measurement?",
             sub: "Optional. Measure around the fullest part, feet together.",
             metric: { min: 75, max: 130, majorEvery: 10 }, imperial: { min: 30, max: 52, majorEvery: 4 },
             start: 100 },
    knuckles: { title: "Measure around your knuckles",
                sub: "Around the widest part of your palm, not including the thumb.",
                metric: { min: 14, max: 28, majorEvery: 2 }, imperial: { min: 5, max: 11, majorEvery: 1 },
                start: 21 },
    hand_length: { title: "And your hand length?", titleFirst: "What is your hand length?",
                   sub: "Optional. From the edge of your palm to the tip of your middle finger.",
                   metric: { min: 14, max: 26, majorEvery: 2 }, imperial: { min: 5, max: 10, majorEvery: 1 },
                   start: 19 }
  };


  /* Which side of a brand's body chart to read for each Attaquer chart. */
  /* Ordered by what Attaquer customers actually own, read off the 495 True Fit
     profiles: MAAP 107 customers, Rapha 38, everything else in single digits or
     absent. Alphabetical buried the two that carry the signal. */
  var BRAND_RANK = { maap: 0, rapha: 1 };

  var BRAND_GROUP = {
    "mens-jersey": "men", "mens-bibs": "men", "mens-outerwear": "men", "warmers": "men",
    "womens-jersey": "women", "womens-bibs": "women", "womens-outerwear": "women"
  };

  /* Readouts are written the way people say them: 5' 10", not 70 in. Each step
     shows the chosen unit large and the other unit small underneath, so the
     shopper never has to convert in their head. */
  function fmtHeight(cm, unit) {
    if (unit === "metric") return Math.round(cm) + " cm";
    var totalIn = Math.round(cm / CM_PER_IN);
    return Math.floor(totalIn / 12) + "' " + (totalIn % 12) + '"';
  }

  function fmtWeight(kg, unit) {
    if (unit === "metric") return Math.round(kg) + " kg";
    return Math.round(kg / KG_PER_LB) + " lb";
  }

  function fmtLength(cm, unit) {
    if (unit === "metric") return Math.round(cm) + " cm";
    var totalIn = cm / CM_PER_IN;
    var whole = Math.floor(totalIn);
    var half = totalIn - whole >= 0.25 && totalIn - whole < 0.75;
    if (totalIn - whole >= 0.75) whole += 1;
    return whole + (half ? "½" : "") + '"';
  }

  var FMT = { height: fmtHeight, weight: fmtWeight, chest: fmtLength };

  function other(unit) { return unit === "metric" ? "imperial" : "metric"; }

  /* A bare range input reads as decoration, not a control. This builds a
     numbered scale whose ticks line up with the thumb's actual travel (a range
     thumb is inset by half its width at each end, so the rail is inset to
     match), and pairs it with a handle you can see and a readout you can type
     into. Discoverable three ways: drag it, tap the number, or use arrow keys.  */
  function buildScale(min, max, majorEvery, labelFor) {
    var scale = el("div", "atqff__scale");
    var inner = el("div", "atqff__scaleInner");
    for (var v = min; v <= max; v++) {
      var pct = ((v - min) / (max - min)) * 100;
      var major = v % majorEvery === 0;
      var tick = el("span", "atqff__tick" + (major ? " is-major" : ""));
      tick.style.left = pct + "%";
      inner.appendChild(tick);
      if (major) {
        var lab = el("span", "atqff__tickLabel text-2xs", labelFor ? labelFor(v) : String(v));
        lab.style.left = pct + "%";
        inner.appendChild(lab);
      }
    }
    scale.appendChild(inner);
    return scale;
  }

  /* Readout doubles as a number input. Commits on blur or Enter, reverts to the
     last good value if what was typed is not usable. */
  function buildReadout(opts) {
    var wrap = el("div", "atqff__readout");
    var field = document.createElement("input");
    field.type = "text";
    field.inputMode = "numeric";
    field.className = "atqff__value";
    field.setAttribute("aria-label", opts.label);
    var suffix = el("span", "atqff__suffix");
    wrap.appendChild(field);
    wrap.appendChild(suffix);
    return { wrap: wrap, field: field, suffix: suffix };
  }

  /* Analytics here runs through Elevar and Shopify Web Pixels, with no gtag or
     GTM container in the theme, so Shopify.analytics.publish is the channel a
     pixel can actually subscribe to. Best effort and silent: a missing tag
     manager must never break the finder. */
  function track(name, payload) {
    try {
      if (window.__isBot) return;
      document.dispatchEvent(new CustomEvent("atqff:" + name, { detail: payload }));
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === "function") {
        window.Shopify.analytics.publish(name, payload);
      }
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        var dl = { event: name };
        Object.keys(payload || {}).forEach(function (k) { dl[k] = payload[k]; });
        window.dataLayer.push(dl);
      }
    } catch (e) { /* never worth an exception */ }
  }

  /* A body does not change between a jersey and a pair of bibs, so asking twice
     is the finder wasting the shopper's time. Kept on the device, never sent
     anywhere on its own, and cleared from the result card. */
  var PROFILE_KEY = "atqff.profile.v1";
  var PROFILE_TTL_DAYS = 365;
  var PROFILE_FIELDS = ["height", "weight", "chest", "waist", "hip",
                        "knuckles", "hand_length", "intent", "brand", "brandSize",
                        "sizingGroup", "unit"];

  function loadProfile() {
    try {
      var raw = window.localStorage.getItem(PROFILE_KEY);
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (!p || !p.at) return null;
      if ((Date.now() - p.at) / 86400000 > PROFILE_TTL_DAYS) {
        window.localStorage.removeItem(PROFILE_KEY);
        return null;
      }
      return p;
    } catch (e) { return null; }
  }

  function saveProfile(patch) {
    try {
      var p = loadProfile() || {};
      Object.keys(patch || {}).forEach(function (k) {
        if (PROFILE_FIELDS.indexOf(k) !== -1 && patch[k] !== null && patch[k] !== undefined) {
          p[k] = patch[k];
        }
      });
      p.at = Date.now();
      window.localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
    } catch (e) { /* private mode, full quota: the finder still works */ }
  }

  function clearProfile() {
    try { window.localStorage.removeItem(PROFILE_KEY); } catch (e) { /* nothing to do */ }
  }

  function Finder(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector("[data-atqff-config]").textContent);
    this.panel = root.querySelector("[data-atqff-panel]");
    this.scrim = root.querySelector("[data-atqff-scrim]");
    this.body = root.querySelector("[data-atqff-body]");
    this.backBtn = root.querySelector("[data-atqff-back]");
    this.progress = root.querySelector("[data-atqff-progress]");
    this.chartView = root.querySelector("[data-atqff-chart]");
    this.unit = defaultUnitFor(this.cfg.defaultUnit);
    this.state = { height: 178, weight: 78, intent: "performance", brand: null, brandSize: null };
    this.answered = {};
    this.restored = {};
    this.sizingGroup = null;
    var saved = loadProfile();
    this.applySizing(this.cfg.unisex ? ((saved && saved.sizingGroup) || "men") : null);
    this.restoreProfile(saved);
    this.step = 0;
    this.bind();
  }

  /* Only the measurements this product's chart actually publishes, in the order
     that matters for the garment. */
  /* Base layers and merino tees are unisex, and their charts convert from the
     shopper's usual Attaquer size, which differs between men's and women's. The
     product cannot tell us which, so the shopper is asked and everything that
     depends on it is rebuilt. */
  Finder.prototype.applySizing = function (group) {
    this.sizingGroup = group;
    if (this.cfg.unisex) {
      var src = group === "women" ? this.cfg.jerseyWomen : this.cfg.jerseyMen;
      this.jerseyHandle = group === "women" ? "womens-jersey" : "mens-jersey";
      this.chart = buildChart(this.jerseyHandle, src,
        group === "women" ? null : this.cfg.weightFallback);
      this.derivedRule = this.cfg.derivedRule || "band-table";
      this.derivedMap = this.derivedRule === "band-table"
        ? baseLayerMap(this.cfg.measurements, group) : null;
    } else {
      this.jerseyHandle = null;
      this.chart = buildChart(this.cfg.chartHandle, this.cfg.measurements, this.cfg.weightFallback);
      this.derivedRule = null;
      this.derivedMap = null;
    }
    if (this.cfg.shoeChart) {
      this.shoe = shoeSystems(this.cfg.measurements);
      if (this.state.shoeSystem === undefined) this.state.shoeSystem = 0;
      if (this.state.shoeSize === undefined) this.state.shoeSize = null;
    }
    this.measures = this.measureSteps();
    this.deferred = this.deferredSteps();
    this.measures.concat(this.deferred).forEach(function (m) {
      if (this.state[m] === undefined) this.state[m] = null;
    }, this);
    this.steps = this.buildSteps();
  };

  Finder.prototype.restoreProfile = function (saved) {
    if (!saved) return;
    var self = this;
    if (saved.unit) this.unit = saved.unit;
    if (saved.intent) this.state.intent = saved.intent;
    if (saved.brand) {
      this.state.brand = saved.brand;
      this.state.brandSize = saved.brandSize || null;
      if (saved.brandSize) this.restored.brand = true;
    }
    ["height", "weight"].concat(this.measures, this.deferred).forEach(function (key) {
      if (saved[key] === undefined || saved[key] === null) return;
      self.state[key] = saved[key];
      self.answered[key] = true;
      self.restored[key] = true;
    });
  };

  /* Enough to answer without asking again. The unisex charts still need the
     men's or women's question unless that was saved too. */
  Finder.prototype.hasSavedAnswer = function () {
    if (this.cfg.shoeChart) return false;
    if (this.cfg.unisex && !(loadProfile() || {}).sizingGroup) return false;
    var self = this;
    var body = this.measures.concat(this.deferred).some(function (m) { return self.restored[m]; });
    return body || (this.restored.brand && !!this.brandMeasurements()) ||
           (!!this.chart.measures.weight && !!this.restored.weight);
  };

  Finder.prototype.shoeStep = function () {
    var self = this;
    var systems = this.shoe || [];
    var sys = systems[this.state.shoeSystem] || systems[0];
    var wrap = el("div", "atqff__step");
    wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", "What shoe size do you wear?"));
    wrap.appendChild(el("p", "atqff__sub text-xs", "These are sized off your shoe size, not your body."));

    if (systems.length > 1) {
      var pick = el("div", "atqff__brands");
      systems.forEach(function (o, i) {
        var b = el("button", "atqff__brand text-2xs" + (i === self.state.shoeSystem ? " is-on" : ""), o.label);
        b.type = "button";
        b.addEventListener("click", function () { self.state.shoeSystem = i; self.render(); });
        pick.appendChild(b);
      });
      wrap.appendChild(pick);
    }

    var range = shoeRange(sys);
    if (this.state.shoeSize === null) this.state.shoeSize = Math.round((range.min + range.max) / 2);
    var readout = el("div", "atqff__readout");
    var field = document.createElement("input");
    field.type = "text"; field.inputMode = "numeric"; field.className = "atqff__value";
    field.setAttribute("aria-label", "Shoe size");
    readout.appendChild(field);

    var input = document.createElement("input");
    input.type = "range"; input.className = "atqff__ruler";
    input.min = range.min; input.max = range.max; input.step = 0.5;
    input.setAttribute("aria-label", "Shoe size");

    var handle = el("div", "atqff__handle");
    handle.setAttribute("aria-hidden", "true");
    var track = el("div", "atqff__track");
    var scaleEl = buildScale(range.min, range.max, 2, null);
    scaleEl.querySelector(".atqff__scaleInner").appendChild(handle);
    track.appendChild(scaleEl);
    track.appendChild(input);

    var paint = function () {
      var v = Math.max(range.min, Math.min(range.max, self.state.shoeSize));
      input.value = v;
      field.value = v;
      field.style.width = (String(v).length + 0.35) + "ch";
      handle.style.left = ((v - range.min) / (range.max - range.min)) * 100 + "%";
    };
    input.addEventListener("input", function () {
      self.state.shoeSize = parseFloat(input.value); paint();
    });
    field.addEventListener("focus", function () { field.select(); });
    var apply = function () {
      var v = parseFloat(field.value);
      if (!isNaN(v)) self.state.shoeSize = v;
      paint();
    };
    field.addEventListener("blur", apply);
    field.addEventListener("keydown", function (e) {
      if (e.key === "Enter") { e.preventDefault(); apply(); field.blur(); }
    });
    paint();

    wrap.appendChild(readout);
    wrap.appendChild(el("p", "atqff__hint text-2xs", "Drag the handle, or tap the number to type it"));
    wrap.appendChild(track);

    var actions = el("div", "atqff__actions");
    var cta = el("button", "atqff__cta _btn _btn-black-md", "Continue");
    cta.type = "button";
    cta.addEventListener("click", function () { self.answered.shoeSize = true; self.next(); });
    actions.appendChild(cta);
    wrap.appendChild(actions);
    return wrap;
  };

  Finder.prototype.shoeRecommend = function () {
    var sys = (this.shoe || [])[this.state.shoeSystem] || (this.shoe || [])[0];
    if (!sys || !this.answered.shoeSize) return { size: null };
    var value = this.state.shoeSize;
    var found = null;
    Object.keys(sys.bands).forEach(function (label) {
      var b = sys.bands[label];
      var lo = b.min === null ? -Infinity : b.min;
      var hi = b.max === null ? Infinity : b.max;
      if (value >= lo && value <= hi && !found) found = label;
    });
    if (!found) return { size: null };
    var fitted = fitToAvailable(normaliseSize(found) || found, this.cfg.productSizes);
    return {
      size: fitted.size || this.matchLabel(found),
      reason: "A " + value + " in " + sys.label.replace(/\s*\(.*\)/, "").toLowerCase() +
              " puts you in " + found + ".",
      notes: fitted.note ? [fitted.note] : []
    };
  };

  Finder.prototype.sizingStep = function () {
    var self = this;
    var wrap = el("div", "atqff__step");
    wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", "Which Attaquer sizing do you use?"));
    wrap.appendChild(el("p", "atqff__sub text-xs",
      "This piece is unisex, and it is sized off your usual Attaquer size, which differs between the men's and women's ranges."));
    [["men", "Men's", "I normally buy Attaquer men's"],
     ["women", "Women's", "I normally buy Attaquer women's"]].forEach(function (opt) {
      var b = el("button", "atqff__choice" + (self.sizingGroup === opt[0] ? " is-on" : ""));
      b.type = "button";
      b.appendChild(el("span", "atqff__choiceTitle font-altehaas text-sm font-bold", opt[1]));
      b.appendChild(el("span", "atqff__choiceSub text-2xs", opt[2]));
      b.addEventListener("click", function () {
        self.applySizing(opt[0]);
        saveProfile({ sizingGroup: opt[0] });
        self.next();
      });
      wrap.appendChild(b);
    });
    return wrap;
  };

  Finder.prototype.measureSteps = function () {
    var have = this.chart.measures;
    var deferred = DEFERRED_MEASURES[this.jerseyHandle || this.cfg.chartHandle] || [];
    return (OPTIONAL_MEASURES[this.jerseyHandle || this.cfg.chartHandle] || [])
      .filter(function (m) {
        return have[m] && MEASURE_STEP[m] && deferred.indexOf(m) === -1;
      });
  };

  Finder.prototype.deferredSteps = function () {
    var have = this.chart.measures;
    return (DEFERRED_MEASURES[this.jerseyHandle || this.cfg.chartHandle] || [])
      .filter(function (m) { return have[m] && MEASURE_STEP[m]; });
  };

  Finder.prototype.buildSteps = function () {
    if (this.cfg.shoeChart) return ["shoe", "result"];
    var steps = [];
    if (this.cfg.unisex) steps.push("sizing");
    /* Height and weight only earn their place when the chart has a weight band
       to match them against. A glove chart does not, so gloves go straight to
       the measurement that matters. */
    if (this.chart.measures.weight) steps.push("height", "weight");
    steps = steps.concat(this.measures);
    if (this.brandOptions().length) steps.push("brand");
    steps.push("intent", "result");
    /* Reachable from the result, never in the walk to it. */
    return steps.concat(this.deferred);
  };

  Finder.prototype.bind = function () {
    var self = this;
    this.root.querySelector("[data-atqff-open]").addEventListener("click", function () { self.open(); });
    this.root.querySelector("[data-atqff-close]").addEventListener("click", function () { self.close(); });
    if (this.backBtn) this.backBtn.addEventListener("click", function () {
      if (self.chartView && !self.chartView.hidden) self.render();
      else self.prev();
    });
    this.scrim.addEventListener("click", function () { self.close(); });
    document.addEventListener("keydown", function (e) {
      if (self.panel.hidden) return;
      if (e.key === "Escape") self.close();
      else self.trapFocus(e);
    });
  };

  /* The trigger lives inside the variant picker, so the panel is born deep in
     the product form where ancestor transforms and stacking contexts let page
     furniture paint straight over a fixed-position drawer. Moving it to body
     the first time it opens puts it in the root stacking context, where
     z-index actually means what it says.

     Move a wrapper carrying the .atqff class, never the panel on its own: a
     large part of this stylesheet is scoped `.atqff .atqff__thing` to keep it
     from leaking into the rest of the theme, and lifting the panel out of that
     ancestor silently unstyles the ruler, the readout and the brand buttons. */
  Finder.prototype.portal = function () {
    if (this.portaled) return;
    var host = document.createElement("div");
    host.className = "atqff atqff__portal";
    host.setAttribute("data-atqff-portal", this.root.getAttribute("data-atqff-id") || "");
    host.appendChild(this.scrim);
    host.appendChild(this.panel);
    document.body.appendChild(host);
    this.host = host;
    this.portaled = true;
  };

  Finder.prototype.open = function () {
    var self = this;
    this.portal();
    this.returnFocusTo = document.activeElement;
    this.panel.hidden = false;
    this.scrim.hidden = false;
    document.documentElement.classList.add("atqff-locked");
    var answerAt = this.steps.indexOf("result");
    this.step = (this.hasSavedAnswer() && answerAt > -1) ? answerAt : 0;
    this.render();
    track("fit_finder_opened", {
      chart: this.chart.handle,
      product_id: this.cfg.productId || null,
      product_title: this.cfg.productTitle || null
    });
    /* Two frames: the first commits the closed state so the transition has
       something to run from, the second flips it open. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.panel.classList.add("is-open");
        self.scrim.classList.add("is-open");
        self.panel.focus({ preventScroll: true });
      });
    });
  };

  Finder.prototype.close = function () {
    var self = this;
    if (this.panel.hidden) return;
    this.panel.classList.remove("is-open");
    this.scrim.classList.remove("is-open");
    document.documentElement.classList.remove("atqff-locked");

    var done = function () {
      self.panel.hidden = true;
      self.scrim.hidden = true;
    };
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { done(); }
    else {
      var fired = false;
      var onEnd = function (e) {
        if (e.target !== self.panel || fired) return;
        fired = true;
        self.panel.removeEventListener("transitionend", onEnd);
        done();
      };
      self.panel.addEventListener("transitionend", onEnd);
      /* Fallback in case the transition never fires (display changes, tab
         backgrounded); without it the panel would stay in the layout. */
      setTimeout(function () { if (!fired) { fired = true; done(); } }, 500);
    }

    if (this.returnFocusTo && this.returnFocusTo.focus) {
      this.returnFocusTo.focus({ preventScroll: true });
    }
  };

  /* Keep Tab inside the dialog while it is open. */
  Finder.prototype.trapFocus = function (e) {
    if (e.key !== "Tab" || this.panel.hidden) return;
    var items = this.panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    var list = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].offsetParent !== null || items[i] === document.activeElement) list.push(items[i]);
    }
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  Finder.prototype.next = function () {
    this.step = Math.min(this.step + 1, this.steps.length - 1);
    this.render();
  };

  Finder.prototype.prev = function () {
    if (this.step === 0) return;
    this.step = Math.max(0, this.step - 1);
    this.render();
  };

  Finder.prototype.go = function (name) {
    var i = this.steps.indexOf(name);
    if (i > -1) { this.step = i; this.render(); }
  };

  Finder.prototype.showChart = function () {
    this.body.hidden = true;
    this.progress.hidden = true;
    this.chartView.hidden = false;
    this.chartView.scrollTop = 0;
    if (this.backBtn) this.backBtn.hidden = false;
    track("fit_finder_chart_viewed", { chart: this.chart.handle, product_id: this.cfg.productId || null });
  };

  Finder.prototype.render = function () {
    var step = this.steps[this.step];
    if (this.chartView) this.chartView.hidden = true;
    this.body.hidden = false;
    this.progress.hidden = false;
    this.body.innerHTML = "";
    if (this.cfg.modelImage && step !== "result") this.body.appendChild(this.modelBlock());

    if (step === "height") this.body.appendChild(this.measureStep({
      key: "height", title: "How tall are you?",
      sub: "Height and weight together give us a starting point.",
      metric:   { min: 140, max: 210, label: "cm",      suffix: "cm", majorEvery: 10 },
      imperial: { min: 55,  max: 83,  label: "ft / in", suffix: "",   majorEvery: 12,
                  labelFor: function (v) { return (v / 12) + "'"; } }
    }));
    else if (step === "weight") this.body.appendChild(this.measureStep({
      key: "weight", title: "What do you weigh?",
      sub: "Attaquer publishes a weight range for each size, so this does real work.",
      metric:   { min: 40, max: 130, label: "kg", suffix: "kg", majorEvery: 10 },
      imperial: { min: 88, max: 287, label: "lb", suffix: "lb", majorEvery: 20 }
    }));
    else if (step === "shoe") this.body.appendChild(this.shoeStep());
    else if (step === "sizing") this.body.appendChild(this.sizingStep());
    else if (step === "brand") this.body.appendChild(this.brandStep());
    else if (step === "intent") this.body.appendChild(this.intentStep());
    else if (step === "result") this.body.appendChild(this.resultStep());
    else this.body.appendChild(this.optionalMeasureStep(step));

    if (this.backBtn) this.backBtn.hidden = this.step === 0;
    this.renderProgress();
  };

  Finder.prototype.renderProgress = function () {
    var self = this;
    this.progress.innerHTML = "";
    /* Steps that sit past the result are reached from it, so they are not part
       of the walk the dots are counting. */
    var end = self.steps.indexOf("result");
    var linear = end === -1 ? self.steps : self.steps.slice(0, end + 1);
    linear.forEach(function (name, i) {
      var dot = document.createElement("span");
      dot.className = "atqff__dot" + (self.step > end || i <= self.step ? " is-done" : "");
      self.progress.appendChild(dot);
    });
  };

  Finder.prototype.modelBlock = function () {
    var wrap = el("div", "atqff__model");
    var img = document.createElement("img");
    img.src = this.cfg.modelImage;
    img.alt = "";
    img.loading = "lazy";
    wrap.appendChild(img);
    if (this.cfg.modelNote) {
      wrap.appendChild(el("p", "atqff__modelnote text-2xs", this.cfg.modelNote));
    }
    return wrap;
  };

  /* One implementation for height, weight and chest. Only the scale and the
     way the number is written change. */
  Finder.prototype.measureStep = function (opts) {
    var self = this;
    var imperial = this.unit === "imperial";
    var scale = imperial ? opts.imperial : opts.metric;
    var isFtIn = imperial && opts.key === "height";

    var wrap = el("div", "atqff__step");
    wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", opts.title));
    wrap.appendChild(el("p", "atqff__sub text-xs", opts.sub));

    var toggle = el("div", "atqff__units");
    [["metric", opts.metric.label], ["imperial", opts.imperial.label]].forEach(function (pair) {
      var on = self.unit === pair[0];
      var b = el("button", "atqff__unit _btn " + (on ? "_btn-black-md" : "_btn-white _btn-md"), pair[1]);
      b.type = "button";
      b.setAttribute("aria-pressed", on ? "true" : "false");
      b.addEventListener("click", function () { self.unit = pair[0]; self.render(); });
      toggle.appendChild(b);
    });
    wrap.appendChild(toggle);

    var readout = el("div", "atqff__readout");
    var fields = [];
    var addField = function (aria, suffix) {
      var f = document.createElement("input");
      f.type = "text";
      f.inputMode = "numeric";
      f.className = "atqff__value";
      f.setAttribute("aria-label", aria);
      readout.appendChild(f);
      readout.appendChild(el("span", "atqff__suffix", suffix));
      fields.push(f);
      return f;
    };

    if (isFtIn) { addField("Feet", "'"); addField("Inches", '"'); }
    else { addField(opts.title, scale.suffix); }

    var input = document.createElement("input");
    input.type = "range";
    input.className = "atqff__ruler";
    input.min = scale.min; input.max = scale.max; input.step = 1;
    input.setAttribute("aria-label", opts.title);

    var handle = el("div", "atqff__handle");
    handle.setAttribute("aria-hidden", "true");

    var track = el("div", "atqff__track");
    var scaleEl = buildScale(scale.min, scale.max, scale.majorEvery, scale.labelFor);
    /* The handle lives in the same coordinate space as the ticks. Positioned on
       the track instead, its percentage would resolve against the padding box
       while the ticks resolve against the inner box, and the two drift apart. */
    scaleEl.querySelector(".atqff__scaleInner").appendChild(handle);
    track.appendChild(scaleEl);
    track.appendChild(input);

    var paint = function () {
      var shown = Math.round(self.displayValue(opts.key));
      shown = Math.max(scale.min, Math.min(scale.max, shown));
      input.value = shown;
      if (isFtIn) {
        fields[0].value = Math.floor(shown / 12);
        fields[1].value = shown % 12;
      } else {
        fields[0].value = shown;
      }
      /* Width follows the digit count; a fixed width clips 3-digit values. */
      fields.forEach(function (f) {
        f.style.width = (String(f.value).length + 0.35) + "ch";
      });
      var pct = ((shown - scale.min) / (scale.max - scale.min)) * 100;
      handle.style.left = pct + "%";
    };

    /* A slider parked on its starting position is not an answer. Without this,
       clicking straight past "What do you weigh?" records 78kg as something the
       shopper told us, which then explains itself back to them as fact and gets
       written to their saved profile and onto the order. */
    var touched = false;
    var commit = function (shown) {
      shown = Math.max(scale.min, Math.min(scale.max, shown));
      touched = true;
      self.setFromDisplay(opts.key, shown);
      paint();
    };

    input.addEventListener("input", function () { commit(parseFloat(input.value)); });

    fields.forEach(function (f) {
      f.addEventListener("focus", function () { f.select(); });
      var apply = function () {
        var shown;
        if (isFtIn) {
          var ft = parseFloat(fields[0].value), inch = parseFloat(fields[1].value);
          if (isNaN(ft)) { paint(); return; }
          shown = ft * 12 + (isNaN(inch) ? 0 : inch);
        } else {
          shown = parseFloat(f.value);
          if (isNaN(shown)) { paint(); return; }
        }
        commit(shown);
      };
      f.addEventListener("blur", apply);
      f.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); apply(); f.blur(); }
      });
    });

    paint();

    wrap.appendChild(readout);
    wrap.appendChild(el("p", "atqff__hint text-2xs", "Drag the handle, or tap the number to type it"));
    wrap.appendChild(track);

    var actions = el("div", "atqff__actions");
    var cta = el("button", "atqff__cta _btn _btn-black-md", opts.ctaLabel || "Continue");
    cta.type = "button";
    var onward = function () { if (opts.after) self.go(opts.after); else self.next(); };
    cta.addEventListener("click", function () {
      if (touched) {
        self.answered[opts.key] = true;
        var patch = { unit: self.unit };
        patch[opts.key] = self.state[opts.key];
        saveProfile(patch);
      }
      onward();
    });
    actions.appendChild(cta);
    if (opts.optional) {
      var skip = el("button", "atqff__skip text-2xs", opts.skipLabel);
      skip.type = "button";
      skip.addEventListener("click", function () {
        self.state[opts.key] = null;
        self.answered[opts.key] = false;
        saveProfile({ unit: self.unit });
        onward();
      });
      actions.appendChild(skip);
    }

    /* The fastest path for a shopper who does not know their measurements is to
       name a brand they already own, so it is offered up front rather than
       buried four questions deep. */
    if (self.isFirstMeasureStep(opts.key) && self.brandOptions().length) {
      var shortcut = el("button", "atqff__skip text-2xs", "Or tell us the size you wear in another brand");
      shortcut.type = "button";
      shortcut.addEventListener("click", function () { self.go("brand"); });
      actions.appendChild(shortcut);
    }
    wrap.appendChild(actions);
    return wrap;
  };

  Finder.prototype.isFirstMeasureStep = function (key) {
    var i = this.steps.indexOf(key);
    if (i < 0) return false;
    for (var j = 0; j < i; j++) {
      if (this.steps[j] !== "sizing") return false;
    }
    return true;
  };

  Finder.prototype.displayValue = function (key) {
    var v = this.state[key];
    if (this.unit === "metric") return v;
    return key === "weight" ? v / KG_PER_LB : v / CM_PER_IN;
  };

  Finder.prototype.setFromDisplay = function (key, v) {
    this.state[key] = this.unit === "metric" ? v : (key === "weight" ? v * KG_PER_LB : v * CM_PER_IN);
  };

  Finder.prototype.optionalMeasureStep = function (key) {
    var conf = MEASURE_STEP[key];
    if (this.state[key] === null) this.state[key] = conf.start;
    var deferred = this.deferred.indexOf(key) !== -1;
    var first = deferred || this.measures.indexOf(key) === 0;
    return this.measureStep({
      key: key,
      title: (first && conf.titleFirst) ? conf.titleFirst : conf.title,
      sub: conf.sub,
      metric:   { min: conf.metric.min,   max: conf.metric.max,   label: "cm", suffix: "cm",
                  majorEvery: conf.metric.majorEvery },
      imperial: { min: conf.imperial.min, max: conf.imperial.max, label: "in", suffix: '"',
                  majorEvery: conf.imperial.majorEvery },
      optional: true,
      after: deferred ? "result" : null,
      ctaLabel: "Use this measurement",
      skipLabel: deferred ? "Never mind" : (first ? "I do not know it" : "Skip this one")
    });
  };

  /* ------------------------------------------------------- brand step ---- */

  /* "I wear M in Rapha" is a body-to-body mapping: every brand here publishes a
     body chart and so does Attaquer, so we read the chest that brand cuts its M
     for and match it against Attaquer's bands. It says what Rapha assumes for a
     Rapha M, not what the rider actually measures, so it never returns high
     confidence on its own. */
  Finder.prototype.brandOptions = function () {
    var table = window.AtqFitFinderBrands;
    var group = BRAND_GROUP[this.jerseyHandle || this.cfg.chartHandle];
    if (!table || !group) return [];
    var out = [];
    Object.keys(table).forEach(function (key) {
      var rows = table[key].groups[group];
      if (rows && Object.keys(rows).length) {
        /* Brand tables are keyed objects, so order is not guaranteed. Sort
           smallest to largest, keeping anything unrecognised at the end. */
        var sizes = Object.keys(rows).sort(function (a, b) {
          var ra = sizeRank(a), rb = sizeRank(b);
          return (ra === null ? 99 : ra) - (rb === null ? 99 : rb);
        });
        out.push({ key: key, label: table[key].label, sizes: sizes });
      }
    });
    out.sort(function (a, b) {
      var ra = BRAND_RANK[a.key] === undefined ? 99 : BRAND_RANK[a.key];
      var rb = BRAND_RANK[b.key] === undefined ? 99 : BRAND_RANK[b.key];
      if (ra !== rb) return ra - rb;
      return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
    });
    return out;
  };

  Finder.prototype.brandMeasurements = function () {
    var table = window.AtqFitFinderBrands;
    var group = BRAND_GROUP[this.jerseyHandle || this.cfg.chartHandle];
    if (!table || !group || !this.state.brand || !this.state.brandSize) return null;
    var brand = table[this.state.brand];
    if (!brand) return null;
    var rows = brand.groups[group];
    return rows && rows[this.state.brandSize] ? rows[this.state.brandSize] : null;
  };

  /* "S-M" on a chart and "S/M" on a product are the same band written two ways,
     so combined labels are matched on their parts rather than as a token. */
  Finder.prototype.matchLabel = function (label) {
    if (!label) return null;
    var want = String(label).toUpperCase().replace(/[\u2013\u2014\/\s]/g, "-").split("-").filter(Boolean).sort().join("-");
    var found = null;
    (this.cfg.productSizes || []).forEach(function (s) {
      var got = String(s).toUpperCase().replace(/[\u2013\u2014\/\s]/g, "-").split("-").filter(Boolean).sort().join("-");
      if (got === want && !found) found = s;
    });
    return found;
  };

  Finder.prototype.brandExplain = function (bm) {
    var table = window.AtqFitFinderBrands;
    var label = (table[this.state.brand] || {}).label || this.state.brand;
    var span = bm.s && bm.s.chest;
    if (!span) return label + " " + this.state.brandSize + " was our starting point.";
    var range = span[0] === span[1]
      ? "a " + sayLength(span[0], this.unit) + " chest"
      : "a " + sayRange(span[0], span[1], this.unit) + " chest";
    return "You told us you wear " + this.state.brandSize + " in " + label +
      ", and " + label + " cut that size for " + range + ".";
  };

  Finder.prototype.brandStep = function () {
    var self = this;
    var options = this.brandOptions();
    var wrap = el("div", "atqff__step");
    wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", "What do you ride in now?"));
    wrap.appendChild(el("p", "atqff__sub text-xs",
      "Optional. If you already own kit from another cycling brand, telling us the size you wear there is the strongest signal we can get."));

    var picked = options.filter(function (o) { return o.key === self.state.brand; })[0];

    if (!picked) {
      var grid = el("div", "atqff__brands");
      options.forEach(function (o) {
        var b = el("button", "atqff__brand text-2xs", o.label);
        b.type = "button";
        b.addEventListener("click", function () {
          self.state.brand = o.key;
          self.state.brandSize = null;
          self.render();
        });
        grid.appendChild(b);
      });
      wrap.appendChild(grid);
    } else {
      wrap.appendChild(el("p", "atqff__brandpick text-xs", "What size do you wear in " + picked.label + "?"));
      var sizes = el("div", "atqff__brands");
      picked.sizes.forEach(function (size) {
        var b = el("button", "atqff__brand text-2xs" + (self.state.brandSize === size ? " is-on" : ""), size);
        b.type = "button";
        b.addEventListener("click", function () {
          self.state.brandSize = size;
          saveProfile({ brand: self.state.brand, brandSize: size });
          self.next();
        });
        sizes.appendChild(b);
      });
      wrap.appendChild(sizes);
      var back = el("button", "atqff__skip text-2xs", "Pick a different brand");
      back.type = "button";
      back.addEventListener("click", function () { self.state.brand = null; self.state.brandSize = null; self.render(); });
      wrap.appendChild(back);
    }

    var actions = el("div", "atqff__actions");
    var skip = el("button", "atqff__skip text-2xs", "I do not own any of these");
    skip.type = "button";
    skip.addEventListener("click", function () {
      self.state.brand = null; self.state.brandSize = null; self.next();
    });
    actions.appendChild(skip);
    wrap.appendChild(actions);
    return wrap;
  };

  Finder.prototype.intentStep = function () {
    var self = this;
    var wrap = el("div", "atqff__step");
    wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", "How do you want it to fit?"));
    wrap.appendChild(el("p", "atqff__sub text-xs", "Attaquer cuts close to the body by default."));

    [
      ["race", "Race", "Second skin. Nothing flapping at speed."],
      ["performance", "Performance", "How Attaquer is designed to fit."],
      ["relaxed", "Relaxed", "A little room to move."]
    ].forEach(function (opt) {
      var b = el("button", "atqff__choice" + (self.state.intent === opt[0] ? " is-on" : ""));
      b.type = "button";
      b.appendChild(el("span", "atqff__choiceTitle font-altehaas text-sm font-bold", opt[1]));
      b.appendChild(el("span", "atqff__choiceSub text-2xs", opt[2]));
      b.addEventListener("click", function () {
        self.state.intent = opt[0];
        saveProfile({ intent: opt[0] });
        self.next();
      });
      wrap.appendChild(b);
    });
    return wrap;
  };

  Finder.prototype.resultStep = function () {
    var self = this;
    var measurements = {};
    /* Defaults are a starting position on a slider, not an answer. Anything the
       shopper skipped past is left out, otherwise a shortcut straight to the
       brand step would silently be answered with a 78kg rider. */
    if (this.chart.measures.weight && this.answered.weight) measurements.weight = this.state.weight;
    var own = false;
    this.measures.concat(this.deferred).forEach(function (m) {
      if (this.answered[m] && this.state[m] !== null && this.state[m] !== undefined) {
        measurements[m] = this.state[m];
        own = true;
      }
    }, this);
    var brandLine = null;
    var derived = {};

    /* Measurements the shopper took themselves beat anything inferred from
       another brand's chart, so brand data is only read when they gave none. */
    if (own) {
      /* nothing to add */
    } else {
      var bm = this.brandMeasurements();
      if (bm && bm.m) {
        if (bm.m.chest) { measurements.chest = bm.m.chest; derived.chest = true; }
        if (bm.m.waist) { measurements.waist = bm.m.waist; derived.waist = true; }
        if (bm.m.hip && this.chart.measures.hip) { measurements.hip = bm.m.hip; derived.hip = true; }
        brandLine = this.brandExplain(bm);
      }
    }

    var rec;
    if (this.cfg.shoeChart) {
      rec = this.shoeRecommend();
      rec.notes = rec.notes || [];
    } else if (this.derivedRule) {
      /* Size against the jersey chart with its own sizes, convert, then fit. */
      rec = recommend(this.chart, measurements, this.state.intent, null, derived, this.unit);
      if (rec.size) {
        rec.jerseySize = rec.size;
        var mapped;
        if (this.derivedRule === "usual-size") {
          /* "Choose your usual size ... size down for a more performance-oriented
             fit." So the tee size is the Attaquer size, one step down for race. */
          var order = SIZE_ORDER;
          var idx = sizeRank(rec.size);
          if (this.state.intent === "race" && idx > 0) idx -= 1;
          mapped = order[idx];
          rec.derivedNote = (this.state.intent === "race")
            ? "Merino tees are cut relaxed, so we have sized you down one from your usual Attaquer " +
              rec.jerseySize + " for the performance fit you asked for."
            : "Merino tees are sized off your usual Attaquer size, and yours is " +
              rec.jerseySize + ".";
        } else {
          mapped = this.derivedMap[normaliseSize(rec.size)];
        }

        /* Try the chart's own band label first ("L-XL" against a product that
           sells "L/XL"). Combined labels do not normalise to a single token, so
           reaching for fitToAvailable first would silently fall through to the
           smallest size. */
        var picked = this.matchLabel(mapped);

        /* Products that sell discrete sizes have no band to match. The chart's
           rule is that a base layer size IS the jersey size, so use it. */
        if (!picked) {
          var fitted = fitToAvailable(normaliseSize(mapped) || normaliseSize(rec.size),
                                      this.cfg.productSizes);
          picked = fitted.size;
          if (fitted.note) rec.notes.push(fitted.note);
        }
        rec.size = picked;
      }
    } else {
      rec = recommend(this.chart, measurements, this.state.intent, this.cfg.productSizes,
                      derived, this.unit);
    }
    var wrap = el("div", "atqff__step atqff__result");

    /* What the shopper actually gave us, which is the dimension that decides
       how much the answer is worth. "weight" is the one we know over-sizes. */
    var path = "none";
    if (this.cfg.shoeChart) path = "shoe";
    else if (own) path = "measured";
    else if (Object.keys(derived).length) path = "brand";
    else if (measurements.weight !== undefined) path = "weight";
    this.lastPath = path;

    document.dispatchEvent(new CustomEvent("atqff:result", { detail: {
      id: this.root.getAttribute("data-atqff-id"),
      chartHandle: this.chart.handle,
      group: this.sizingGroup || null,
      intent: this.state.intent,
      measurements: measurements,
      size: rec.size,
      path: path,
      confidence: rec.confidence || null,
      shoe: this.answered.shoeSize ? { system: this.state.shoeSystem, size: this.state.shoeSize } : null
    } }));

    track("fit_finder_result", {
      chart: this.chart.handle,
      product_id: this.cfg.productId || null,
      recommended_size: rec.size || null,
      path: path,
      intent: this.state.intent,
      confidence: rec.confidence || null,
      units: this.unit
    });

    if (!rec.size) {
      wrap.appendChild(el("h2", "atqff__q font-altehaas text-lg font-bold", "We need a little more to go on"));
      wrap.appendChild(el("p", "atqff__sub text-xs",
        "Give us one measurement, or tell us a size you wear in another brand, and we can size you."));

      var firstUnanswered = this.measures[0];
      if (firstUnanswered) {
        var give = el("button", "atqff__cta _btn _btn-black-md",
          "Add your " + (MEASURE_LABEL[firstUnanswered] || firstUnanswered));
        give.type = "button";
        give.addEventListener("click", function () { self.go(firstUnanswered); });
        wrap.appendChild(give);
      }
      if (this.brandOptions().length) {
        var toBrand = el("button", "atqff__skip text-2xs", "Tell us a brand you already wear");
        toBrand.type = "button";
        toBrand.addEventListener("click", function () { self.go("brand"); });
        wrap.appendChild(toBrand);
      }
      var back = el("button", "atqff__skip text-2xs", "Start again");
      back.type = "button";
      back.addEventListener("click", function () { self.step = 0; self.render(); });
      wrap.appendChild(back);
      return wrap;
    }

    wrap.appendChild(el("p", "atqff__resultLabel text-2xs uppercase", "Your size"));
    wrap.appendChild(el("div", "atqff__size font-altehaas", rec.size));

    if (brandLine) wrap.appendChild(el("p", "atqff__why text-xs", brandLine));
    wrap.appendChild(el("p", "atqff__why text-xs", rec.reason));
    if (rec.jerseySize) {
      wrap.appendChild(el("p", "atqff__why text-xs", rec.derivedNote ||
        ("Base layers are sized off your jersey size, so a " + rec.jerseySize +
         " in Attaquer jerseys puts you in " + rec.size + " here.")));
    }
    rec.notes.forEach(function (n) { wrap.appendChild(el("p", "atqff__note text-2xs", n)); });

    if (Object.keys(this.restored).length) {
      wrap.appendChild(el("p", "atqff__note text-2xs",
        "We used the measurements you gave us last time."));
    }

    var modelLine = modelNoteFor(this.cfg.modelNote, rec.size);
    if (modelLine) wrap.appendChild(el("p", "atqff__model-line text-2xs", modelLine));

    var actions = el("div", "atqff__actions");
    var apply = el("button", "atqff__cta _btn _btn-black-md", this.cfg.productId ? "Select " + rec.size : "See my sizes");
    apply.type = "button";
    apply.addEventListener("click", function () {
      track("fit_finder_applied", {
        chart: self.chart.handle,
        product_id: self.cfg.productId || null,
        recommended_size: rec.size,
        path: path,
        intent: self.state.intent,
        confidence: rec.confidence || null
      });
      self.stampCart(rec, path);
      self.selectVariant(rec.size);
      self.close();
    });
    actions.appendChild(apply);

    var firstMeasure = this.measures[0];
    if (firstMeasure && !this.answered[firstMeasure]) {
      var label = MEASURE_LABEL[firstMeasure] || firstMeasure;
      var refine = el("button", "atqff__skip text-2xs", "Add your " + label + " to sharpen this");
      refine.type = "button";
      refine.addEventListener("click", function () { self.go(firstMeasure); });
      actions.appendChild(refine);
    }

    this.deferred.forEach(function (m) {
      if (self.answered[m]) return;
      var extra = el("button", "atqff__skip text-2xs",
        "Add your " + (MEASURE_LABEL[m] || m) + " if it runs wide");
      extra.type = "button";
      extra.addEventListener("click", function () { self.go(m); });
      actions.appendChild(extra);
    });

    if (this.chartView) {
      var chartLink = el("button", "atqff__skip text-2xs", "See the full size chart");
      chartLink.type = "button";
      chartLink.addEventListener("click", function () { self.showChart(); });
      actions.appendChild(chartLink);
    }

    var restart = el("button", "atqff__skip text-2xs", "Start again");
    restart.type = "button";
    restart.addEventListener("click", function () { self.step = 0; self.render(); });
    actions.appendChild(restart);

    if (Object.keys(this.restored).length) {
      var forget = el("button", "atqff__skip text-2xs", "Forget my measurements");
      forget.type = "button";
      forget.addEventListener("click", function () {
        clearProfile();
        self.restored = {};
        self.answered = {};
        self.measures.concat(self.deferred).forEach(function (m) { self.state[m] = null; });
        self.state.brand = null;
        self.state.brandSize = null;
        self.step = 0;
        self.render();
      });
      actions.appendChild(forget);
    }

    wrap.appendChild(actions);
    return wrap;
  };

  /* An event stream says how many people used it. Only something that survives
     to the order says whether it worked, because the answer lives in the return
     reasons. One hidden cart attribute, written when a shopper accepts a size,
     splits every future order into finder-assisted and not, and records which
     path produced the answer. Underscore-prefixed so it stays out of the
     customer's confirmation. */
  /* The measurements themselves, not just the size we named. Without these an
     order says what we recommended; with them it says what body we recommended
     it for, which is the only way a return reason can teach us anything. */
  Finder.prototype.bodyStamp = function () {
    var self = this;
    var bits = [];
    ["height", "weight", "chest", "waist", "hip"].forEach(function (key) {
      var v = self.state[key];
      if (!self.answered[key] || v === null || v === undefined || isNaN(v)) return;
      bits.push(key + "=" + Math.round(v * 10) / 10);
    });
    return bits.length ? bits.join(";") : null;
  };

  Finder.prototype.stampCart = function (rec, path) {
    if (window.__isBot) return;
    var self = this;
    var stamp = [this.cfg.productId || "", this.chart.handle, rec.size, path,
                 this.state.intent, rec.confidence || ""].join(":");
    try {
      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var prior = String((cart.attributes || {})._fit_finder || "").split(";").filter(Boolean);
          if (prior.indexOf(stamp) !== -1) return null;
          prior.push(stamp);
          while (prior.length > 5 || prior.join(";").length > 240) prior.shift();
          var attributes = { _fit_finder: prior.join(";") };
          var body = self.bodyStamp();
          if (body) attributes._fit_body = body;
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: attributes })
          });
        })
        .catch(function () { /* measurement never blocks a sale */ });
    } catch (e) { /* same */ }
  };

  /* Click the matching size control in the theme's own variant picker so the
     product form, price and inventory all update through the normal path. */
  Finder.prototype.selectVariant = function (size) {
    var target = String(size).trim().toUpperCase();
    var scope = document.querySelector("variant-selects") || document;

    var radios = scope.querySelectorAll('fieldset[data-type="radio"] input[type="radio"], fieldset input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var label = (r.value || "").trim().toUpperCase();
      if (label === target && !r.checked) {
        r.checked = true;
        r.dispatchEvent(new Event("change", { bubbles: true }));
        r.dispatchEvent(new Event("input", { bubbles: true }));
        return true;
      }
      if (label === target) return true;
    }

    var selects = scope.querySelectorAll("select");
    for (var j = 0; j < selects.length; j++) {
      var sel = selects[j];
      for (var k = 0; k < sel.options.length; k++) {
        if ((sel.options[k].value || "").trim().toUpperCase() === target) {
          sel.selectedIndex = k;
          sel.dispatchEvent(new Event("change", { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }

  function init() {
    document.querySelectorAll("[data-atqff]").forEach(function (root) {
      if (root.__atqff) return;
      try { root.__atqff = new Finder(root); }
      catch (e) { if (window.console) console.warn("fit finder failed to start", e); }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  document.addEventListener("shopify:section:load", init);

  window.AtqFitFinder = {
    normaliseSize: normaliseSize,
    sizeRank: sizeRank,
    parseCell: parseCell,
    buildChart: buildChart,
    matchMeasure: matchMeasure,
    recommend: recommend,
    modelNoteFor: modelNoteFor,
    CALIBRATION: CALIBRATION
  };
})();
